<script lang="ts">
  import { wbsLabel } from "../lib/wbs";
  import { createEventDispatcher } from "svelte";
  import { openEditModal, wbsNodes, canEdit, subcontractorColors, twoShifts } from "../lib/stores";
  import { subcontractorColor } from "../lib/subcontractors";
  import type { StatusRow, Task } from "../lib/types";

  export let t: Task;
  export let critical: boolean;
  export let span: number;
  export let spanIdx: number;
  /** Site-status requirements this card's day doesn't currently satisfy — e.g. main
   *  trafo power still "On" on an E-room day. A warning only; nothing here can change
   *  the status row itself. */
  export let statusConflicts: { row: StatusRow; wantLabel: string }[] = [];
  /** This day's work has been marked done. Full card redesign is still to come — this
   *  is the minimum needed so finished work doesn't read as still outstanding. */
  export let done = false;
  /** Off for print, where drag and click affordances are meaningless on paper. */
  export let interactive = true;
  /** Doesn't match the current filter. Dimmed rather than hidden, so a match keeps the
   *  surrounding days that make it possible to place. */
  export let dimmed = false;
  /** Bulk edit is on: a click (or Enter/Space) toggles selection instead of opening the
   *  edit modal, and dragging is turned off so a click can't be mistaken for a drag. */
  export let selectable = false;
  export let selected = false;

  const dispatch = createEventDispatcher<{
    reorder: { draggedId: string; targetId: string };
    move: { id: string; dayDelta: number; phaseDelta: number; shiftDelta: number };
    unpin: { id: string };
    toggleSelect: { id: string };
  }>();

  $: editable = $canEdit && interactive && !selectable;
  $: pinned = t.pinnedDay != null && !isNaN(t.pinnedDay);
  // Day 0 is indistinguishable from "no pin" once scheduled — CPM's `es = Math.max(es, pin)`
  // floor is a no-op at 0, so the task is still fully auto-planned. Showing "d0" would
  // read as a constraint that isn't actually there.
  $: showPinnedBadge = pinned && t.pinnedDay !== 0;
  $: statusWarningTitle = statusConflicts
    .map((c) => `${c.row.label} must be ${c.wantLabel}`)
    .join(" · ");

  function onClick() {
    if (selectable) dispatch("toggleSelect", { id: t.id });
    else openEditModal(t.id);
  }

  /**
   * Alt+arrows give the board a keyboard equivalent of the drag: left/right shifts the
   * day, up/down changes phase lane. Alt+Shift+up/down instead moves between the two
   * daily shifts, on a project that uses them. Drag was the only way to reschedule,
   * which left the board unusable without a mouse and awkward on a touchscreen.
   */
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); return; }
    if (!editable) return;
    if (e.altKey) {
      const dayDelta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (e.shiftKey) {
        const shiftDelta = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
        if (dayDelta || shiftDelta) {
          e.preventDefault();
          dispatch("move", { id: t.id, dayDelta, phaseDelta: 0, shiftDelta });
        }
        return;
      }
      const phaseDelta = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
      if (dayDelta || phaseDelta) {
        e.preventDefault();
        dispatch("move", { id: t.id, dayDelta, phaseDelta, shiftDelta: 0 });
      }
      return;
    }
    if (pinned && (e.key === "Delete" || e.key === "Backspace")) {
      e.preventDefault();
      dispatch("unpin", { id: t.id });
    }
  }

  function onUnpin(e: MouseEvent) {
    e.stopPropagation();
    dispatch("unpin", { id: t.id });
  }
  function onDragStart(e: DragEvent) {
    e.dataTransfer?.setData("text/plain", t.id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    (e.currentTarget as HTMLElement).classList.add("dragging");
  }
  function onDragEnd(e: DragEvent) {
    (e.currentTarget as HTMLElement).classList.remove("dragging");
  }
  // Dropping one card directly onto another reorders it next to that card (and still
  // reschedules it to this cell's day/phase, same as dropping on the cell background) —
  // stop the event reaching the cell's own drop handler so it isn't also processed there.
  // `dropTarget` is what tells you that landing here means something different from
  // landing on the cell around it; without it the behaviour was invisible.
  let dropTarget = false;
  function onCardDragOver(e: DragEvent) {
    if (!interactive) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    dropTarget = true;
  }
  function onCardDragLeave() { dropTarget = false; }
  function onCardDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dropTarget = false;
    const draggedId = e.dataTransfer?.getData("text/plain");
    if (!draggedId || draggedId === t.id) return;
    dispatch("reorder", { draggedId, targetId: t.id });
  }
</script>

<div
  class="sticky"
  class:done
  class:blocked={t.blocked && !done}
  class:milestone={t.type === "milestone"}
  class:drop-target={dropTarget}
  class:dimmed
  class:selectable
  class:selected={selectable && selected}
  style={done || t.blocked || !t.subcontractors.length ? "" : `border-left-color:${subcontractorColor($subcontractorColors, t.subcontractors[0])}`}
  draggable={editable}
  tabindex={0}
  role="button"
  aria-pressed={selectable ? selected : undefined}
  aria-label={selectable
    ? `${wbsLabel(t, $wbsNodes)} ${t.name}${selected ? ", selected" : ""}. Enter to ${selected ? "deselect" : "select"}.`
    : `${wbsLabel(t, $wbsNodes)} ${t.name}${showPinnedBadge ? `, pinned to day ${t.pinnedDay}` : ""}${statusWarningTitle ? `. ${statusWarningTitle}` : ""}. Enter to edit${editable ? ", Alt with arrow keys to reschedule" : ""}.`}
  title={selectable ? undefined : (editable ? `Enter to edit · Alt+← / Alt+→ to move a day · Alt+↑ / Alt+↓ to change phase${$twoShifts ? " · Alt+Shift+↑ / Alt+Shift+↓ to change shift" : ""}` : undefined)}
  on:click={() => interactive && onClick()}
  on:keydown={(e) => interactive && onKeydown(e)}
  on:dragstart={onDragStart}
  on:dragend={onDragEnd}
  on:dragover={onCardDragOver}
  on:dragleave={onCardDragLeave}
  on:drop={onCardDrop}
>
  <div class="sticky-top">
    {#if selectable}
      <input class="sticky-checkbox" type="checkbox" checked={selected} tabindex="-1" aria-hidden="true" />
    {/if}
    <span class="sticky-id-group">
      <span class="sticky-id">{wbsLabel(t, $wbsNodes)}</span>
      <!-- Critical path is a planning fact (zero slack), not an alarm — a small dot
           says so without the weight of a red border/background/badge on the whole card. -->
      {#if critical}<span class="crit-dot" title="Critical path — zero slack"></span>{/if}
    </span>
    {#if done}
      <span class="sticky-badge done">✓ Done</span>
    {:else if t.blocked}
      <span class="sticky-badge blocked">⚑ Blocked</span>
    {:else if t.type === "milestone"}
      <span class="sticky-badge">Milestone</span>
    {/if}
    {#if showPinnedBadge}
      {#if editable}
        <button
          class="sticky-badge pinned"
          type="button"
          title="Pinned: can't start before day {t.pinnedDay}. Click to unpin and go back to automatic scheduling."
          on:click={onUnpin}
        >📌 d{t.pinnedDay} ✕</button>
      {:else}
        <span class="sticky-badge pinned">📌 d{t.pinnedDay}</span>
      {/if}
    {/if}
  </div>
  <div class="sticky-name">{t.name}</div>
  {#if t.subcontractors.length}
    <!-- Visible even at rest — the whole point of a subcontractor's color is spotting
         their cards on the board at a glance, which a hover-to-reveal would defeat. -->
    <div class="sticky-tags">
      {#each t.subcontractors as sc}
        <span class="sticky-tag subc" style="background:{subcontractorColor($subcontractorColors, sc)}">{sc}</span>
      {/each}
    </div>
  {/if}
  <!-- Compact by default — everything else here only shows up once you're actually
       looking at this card (hover or keyboard focus), so the board reads sparser at rest. -->
  <div class="sticky-detail">
    <div class="sticky-meta">
      {#if span > 1}
        <span>day {spanIdx}/{span}</span>
      {/if}
      {#if t.preds.length}
        <span>← {t.preds.join(", ")}</span>
      {/if}
    </div>
    {#if t.responsibleParty}
      <div class="sticky-tags">
        <span class="sticky-tag party">{t.responsibleParty}</span>
      </div>
    {/if}
    {#if t.requiredEquipment}
      <div class="sticky-equipment">🔧 {t.requiredEquipment}</div>
    {/if}
    {#if t.requiredPersonnel}
      <div class="sticky-equipment">👷 {t.requiredPersonnel}</div>
    {/if}
    {#if statusConflicts.length}
      <div class="sticky-equipment status-alert" title={statusWarningTitle}>⚡ {statusWarningTitle}</div>
    {/if}
  </div>
</div>
